import { defineStore } from 'pinia'

import { setUnauthorizedHandler } from '@/lib/api'
import { auth as authApi } from '@/lib/endpoints'

// Roles that may use management. Publisher and network accounts have their own apps.
export const STAFF_ROLES = ['admin', 'organization', 'developer', 'account_manager', 'ad_operation']

function decode(jwt) {
  const part = jwt.split('.')[1]
  const json = decodeURIComponent(atob(part.replace(/-/g, '+').replace(/_/g, '/')).split('').map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''))
  return JSON.parse(json)
}

let appRouter = null

function stored(key) {
  try { return localStorage.getItem(key) } catch (e) { return null }
}

export const useAuth = defineStore('auth', {
  state: () => {
    let user = null
    try { user = JSON.parse(stored('user') || 'null') } catch (e) { user = null }
    return { token: stored('accessToken'), user }
  },
  getters: {
    isAuthenticated: (s) => !!s.token && !!s.user,
    role: (s) => s.user?.role || '',
    isAdmin: (s) => s.user?.role === 'admin',
    displayName: (s) => [s.user?.first_name, s.user?.last_name].filter(Boolean).join(' ') || s.user?.username || '',
  },
  actions: {
    async login(username, password) {
      const { access } = await authApi.login(username.trim().toLowerCase(), password)
      const claims = decode(access)
      const user = claims.user || {}
      if (!STAFF_ROLES.includes(user.role)) {
        const err = new Error('This account cannot use management. Publisher and network accounts sign in on their own dashboards.')
        err.code = 'role'
        throw err
      }
      this.token = access
      this.user = user
      try {
        localStorage.setItem('accessToken', access)
        localStorage.setItem('user', JSON.stringify(user))
      } catch (e) { /* storage blocked: session lasts this tab only */ }
    },
    setUser(patch) {
      this.user = { ...this.user, ...patch }
      try { localStorage.setItem('user', JSON.stringify(this.user)) } catch (e) { /* ignore */ }
    },
    clear() {
      this.token = null
      this.user = null
      try { localStorage.removeItem('accessToken'); localStorage.removeItem('user') } catch (e) { /* ignore */ }
    },
    async logout() {
      if (this.token) authApi.logout().catch(() => {})
      this.clear()
      appRouter?.push({ name: 'login' })
    },
    /** Called once at startup: a 401 anywhere ends the session. */
    install(router) {
      appRouter = router
      setUnauthorizedHandler(() => {
        if (!this.token) return
        this.clear()
        router.push({ name: 'login', query: { next: router.currentRoute.value.fullPath, expired: '1' } })
      })
    },
  },
})
